import { useNavigate } from 'react-router';
import {
  PageHeader,
  Card,
  CardContent,
  DataTable,
  Badge,
  type Column,
} from '@redbax/ui-kit';
import { CheckCircle2, XCircle } from 'lucide-react';
import type { PackageRegistry } from '@redbax/shared';
import {
  useAdminPackageRegistries,
  useToggleAdminPackageRegistry,
} from '../api/admin-package-hooks';

const columns = (
  onToggle: (id: string) => void,
): Column<PackageRegistry>[] => [
  { key: 'name', header: 'Name', sortable: true },
  {
    key: 'ecosystem',
    header: 'Ecosystem',
    sortable: true,
    render: (row) => <Badge variant="secondary">{row.ecosystem}</Badge>,
  },
  {
    key: 'isEnabled',
    header: 'Enabled',
    render: (row) => (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggle(row.id);
        }}
        className="flex items-center gap-1"
        aria-label={row.isEnabled ? 'Disable registry' : 'Enable registry'}
      >
        {row.isEnabled ? (
          <CheckCircle2 className="h-4 w-4 text-green-ok" />
        ) : (
          <XCircle className="h-4 w-4 text-text-secondary" />
        )}
      </button>
    ),
  },
  {
    key: 'domains',
    header: 'Domains',
    render: (row) => (
      <span className="text-text-secondary">
        {row.domains.length} domain{row.domains.length !== 1 ? 's' : ''}
      </span>
    ),
  },
];

export function AdminPackageRegistriesPage() {
  const navigate = useNavigate();
  const { data: registries, isLoading } = useAdminPackageRegistries();
  const toggleRegistry = useToggleAdminPackageRegistry();

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Package Registries"
          description="Manage package registry sources for vulnerability monitoring"
        />
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded bg-surface-raised" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Package Registries"
        description="Manage package registry sources for vulnerability monitoring"
      />

      <Card>
        <CardContent className="pt-6">
          <DataTable
            columns={columns((id) => toggleRegistry.mutate({ id }))}
            data={registries ?? []}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            emptyMessage="No package registries configured"
            onRowClick={(row) => navigate(`/package-registries/${row.id}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
